<template>
  <admin-layout :title="$t('edit_x', { x: $t('User') })">
    <tec-form-section @submitted="update">
      <template #title>
        <div class="flex items-center">
          <Link class="text-indigo-400 hover:text-indigo-600" :href="route('users')">{{ $t('Users') }}</Link>
          <span class="text-indigo-400 font-medium mx-2">/</span>
          <img v-if="edit.photo" class="block w-6 h-6 rounded-full mr-2" :src="edit.photo" />
          {{ edit.name }}
        </div>
      </template>

      <template #description>{{ $t('Update the record by modifying the details in the form below') }}</template>

      <template #form>
        <trashed-message v-if="edit.deleted_at" class="mb-6" @restore="restore">{{ $t('This user has been deleted.') }} </trashed-message>

        <div class="col-span-full lg:col-span-4 grid gap-y-6 divide-y divide-gray-200">
          <div class="grid gap-y-6">
            <text-input
              :label="$t('Name')"
              v-model="form.name"
              :error="$page.props.errors.name ? $page.props.errors.name.replace('{attribute}', 'name') : null"
            />
            <text-input
              :label="$t('Username')"
              v-model="form.username"
              :error="$page.props.errors.username ? $page.props.errors.username.replace('{attribute}', 'username') : null"
            />
            <text-input
              type="email"
              :label="$t('Email')"
              v-model="form.email"
              :error="$page.props.errors.email ? $page.props.errors.email.replace('{attribute}', 'email') : null"
            />
          </div>
          <div class="grid gap-y-6 pt-6">
            <text-input
              id="password"
              type="password"
              :label="$t('Password')"
              v-model="form.password"
              :error="$page.props.errors.password ? $page.props.errors.password.replace('{attribute}', 'password') : null"
            />
            <text-input
              type="password"
              id="password_confirmation"
              :label="$t('Confirm Password')"
              v-model="form.password_confirmation"
              :error="
                $page.props.errors.password_confirmation
                  ? $page.props.errors.password_confirmation.replace('{attribute}', 'confirm password')
                  : null
              "
            />
          </div>
        </div>
        <!-- <select-input v-model="form.owner" :error="$page.props.errors.owner" class="pr-6 pb-8 w-full lg:w-1/2" label="Owner">
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select-input> -->
        <div v-if="roles && roles.length" class="col-span-full flex flex-wrap items-center gap-x-6 gap-y-3">
          <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Roles') }}</label>
          <template v-for="role in roles" :key="role.name">
            <check-box
              :id="role.name"
              :value="role.name"
              :label="$t(role.name)"
              v-model:checked="form.roles"
              :error="$page.props.errors.roles ? $page.props.errors.roles.replace('{attribute}', 'roles') : null"
            ></check-box>
          </template>
          <tec-input-error
            class="w-full mt-1"
            v-if="$page.props.errors.roles"
            :message="$page.props.errors.roles.replace('{attribute}', 'roles').replace('{min}', '1')"
          />
        </div>
      </template>

      <template #actions>
        <div class="w-full flex items-center justify-between">
          <button
            type="button"
            @click="destroy"
            v-if="!edit.deleted_at"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('User') }) }}
          </button>
          <button
            v-else
            type="button"
            @click="deletePermanently"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Permanently') }) }}
          </button>
          <div class="flex items-center">
            <tec-action-message :on="form.recentlySuccessful" class="mr-3">{{ $t('Saved.') }}</tec-action-message>
            <loading-button type="submit" :loading="form.processing" :disabled="form.processing">{{ $t('Save') }}</loading-button>
          </div>
        </div>
      </template>
    </tec-form-section>

    <!-- Delete User Confirmation Modal -->
    <Dialog
      max-width="md"
      :show="permanent"
      action-type="delete"
      title="Delete User?"
      :close="closePermanentModal"
      :action="deleteUserPermanently"
      action-text="Delete Permanently"
      :content="`<p class='mb-2'>${$t('Are you sure you want to delete the record permanently?')}</p>
        <p>${$t('Once deleted, all of its resources and data will be permanently deleted.')}</p>`"
    />

    <!-- Delete User Confirmation Modal -->
    <Dialog
      :show="confirm"
      :close="closeModal"
      :action="deleteUser"
      action-type="delete"
      title="Delete User?"
      action-text="Delete User"
      :content="$t('Are you sure you want to delete the record?')"
    />

    <!-- Restore User Confirmation Modal -->
    <Dialog
      :show="restoreConf"
      :action="restoreUser"
      title="Restore User!"
      :close="closeRestoreModal"
      action-text="Restore User"
      :content="$t('Are you sure you want to restore the record?')"
    />
  </admin-layout>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';

import Dialog from '@/Shared/Dialog';
import CheckBox from '@/Shared/CheckBox';
import TextInput from '@/Shared/TextInput';
import FileInput from '@/Shared/FileInput';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import LoadingButton from '@/Shared/LoadingButton';
import TecInputError from '@/Jetstream/InputError';
import TecDialogModal from '@/Jetstream/DialogModal';
import TrashedMessage from '@/Shared/TrashedMessage';
import TecFormSection from '@/Jetstream/FormSection';
import TecDangerButton from '@/Jetstream/DangerButton';
import TecSectionTitle from '@/Jetstream/SectionTitle';
import TecActionMessage from '@/Jetstream/ActionMessage';
import TecSecondaryButton from '@/Jetstream/SecondaryButton';

export default {
  props: ['edit', 'roles'],

  components: {
    Link,
    Dialog,
    CheckBox,
    FileInput,
    TextInput,
    AdminLayout,
    SelectInput,
    LoadingButton,
    TecInputError,
    TecDialogModal,
    TecFormSection,
    TecDangerButton,
    TrashedMessage,
    TecSectionTitle,
    TecActionMessage,
    TecSecondaryButton,
  },

  data() {
    return {
      confirm: false,
      permanent: false,
      restoreConf: false,
      form: this.$inertia.form({
        _method: 'put',
        photo: null,
        password: null,
        name: this.edit.name,
        email: this.edit.email,
        password_confirmation: null,
        username: this.edit.username,
        roles: this.edit.roles,
      }),
    };
  },

  methods: {
    update() {
      this.form.post(this.route('users.update', this.edit.id), {
        preserveScroll: true,
        onSuccess: () => this.form.reset('password', 'password_confirmation', 'photo'),
      });
    },
    destroy() {
      this.confirm = true;
      // if (confirm('Are you sure you want to delete this user?')) {
      //   this.$inertia.delete(this.route('users.destroy', this.edit.id));
      // }
    },
    restore() {
      this.restoreConf = true;
      // if (confirm('Are you sure you want to restore this user?')) {
      //   this.$inertia.put(this.route('users.restore', this.edit.id));
      // }
    },
    deletePermanently() {
      this.permanent = true;
    },
    // confirmUserDeletion() {
    //   this.confirm = true;
    //   setTimeout(() => this.$refs.password.focus(), 250);
    // },
    restoreUser() {
      this.$inertia.put(this.route('users.restore', this.edit.id), {
        onSuccess: () => (this.restoreConf = false),
        // onError: () => this.$refs.password.focus(),
        // onFinish: () => this.form.reset(),
      });
    },
    deleteUser() {
      this.form.delete(route('users.destroy', this.edit.id), {
        onSuccess: () => this.closeModal(),
        // onError: () => this.$refs.password.focus(),
        // onFinish: () => this.form.reset(),
      });
    },
    deleteUserPermanently() {
      this.form.delete(route('users.destroy.permanently', this.edit.id), {
        onSuccess: () => this.closeModal(),
        // onError: () => this.$refs.password.focus(),
        // onFinish: () => this.form.reset(),
      });
    },
    closeRestoreModal() {
      this.restoreConf = false;
    },
    closePermanentModal() {
      this.permanent = false;
    },
    closeModal() {
      this.confirm = false;
    },
  },
};
</script>
